import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../features/auth/AuthContext';
import API_BASE from '../services/apiBase';
import {
  Mail,
  Eye,
  EyeOff,
  LogIn,
  AlertCircle,
  CheckCircle,
} from 'lucide-react';
import AuthSplitShell from '../components/AuthSplitShell';
import GoogleAuthButton from '../components/GoogleAuthButton';

/** Customer & staff sign-in — administrators use /admin/login */
const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });

  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email.trim())) {
        newErrors.email = 'Please enter a valid email address';
      }
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const res = await fetch(`${API_BASE}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: formData.email.trim().toLowerCase(),
          password: formData.password,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setErrors({ general: data.message || 'Invalid credentials. Please try again.' });
        return;
      }
      const u = data.data?.user;
      const token = data.data?.token;
      if (!u || !token) {
        setErrors({ general: 'Unexpected server response. Please try again.' });
        return;
      }
      const loggedInUser = {
        ...u,
        token,
        loginId: u.email,
        loginType: 'email',
        isLoggedIn: true,
        staffDepartment: u.staffDepartment || 'full',
      };
      login(loggedInUser);
      if (u.role === 'staff') {
        navigate('/admin', { replace: true, state: { user: loggedInUser } });
        return;
      }
      navigate('/profile', { replace: true, state: { user: loggedInUser } });
    } catch (error) {
      console.error('Login error:', error);
      setErrors({ general: 'Invalid credentials. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AuthSplitShell
      panel
      title="Welcome back"
      subtitle="Sign in to your Jet Shipping Cargo account"
      heroLead="Welcome back — track shipments, manage your profile, and stay updated on your cargo."
    >
      {location.state?.message && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="auth-split-msg-success"
        >
          <CheckCircle className="w-5 h-5 flex-shrink-0" />
          {location.state.message}
        </motion.div>
      )}

      <motion.div
        className="auth-split-card"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45 }}
      >
        <form onSubmit={handleSubmit} noValidate>
          <div className="auth-split-field">
            <label htmlFor="login-email">Email address *</label>
            <div className="auth-split-field__wrap">
              <input
                id="login-email"
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className={`form-input ${errors.email ? 'error' : ''}`}
                placeholder="Enter your email address"
                autoComplete="email"
              />
              <Mail className="w-4 h-4 auth-split-field__icon" aria-hidden />
            </div>
            {errors.email ? <span className="error-text">{errors.email}</span> : null}
          </div>

          <div className="auth-split-field">
            <label htmlFor="login-password">Password *</label>
            <div className="auth-split-field__wrap">
              <input
                id="login-password"
                type={showPassword ? 'text' : 'password'}
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                className={`form-input ${errors.password ? 'error' : ''}`}
                placeholder="Enter your password"
                autoComplete="current-password"
              />
              <button
                type="button"
                className="auth-split-field__toggle"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            {errors.password ? <span className="error-text">{errors.password}</span> : null}
          </div>

          {errors.general ? (
            <div className="auth-split-msg-error">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              {errors.general}
            </div>
          ) : null}

          <div className="auth-split-login-row">
            <label>
              <input type="checkbox" name="remember" />
              <span>Remember me</span>
            </label>
            <Link to="/forgot-password" className="auth-split-links">
              Forgot password?
            </Link>
          </div>

          <div style={{ textAlign: 'center' }}>
            <button
              type="submit"
              disabled={isSubmitting}
              className="btn auth-split-login-submit"
              style={{
                opacity: isSubmitting ? 0.7 : 1,
                cursor: isSubmitting ? 'not-allowed' : 'pointer',
              }}
            >
              {isSubmitting ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', justifyContent: 'center' }}>
                  <span className="auth-split-spinner" />
                  Signing in...
                </div>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', justifyContent: 'center' }}>
                  <LogIn className="w-5 h-5" aria-hidden />
                  Sign in
                </div>
              )}
            </button>
          </div>
        </form>

        <div className="auth-split-divider-or" aria-hidden>
          <span>or continue with</span>
        </div>
        <div className="auth-split-social-row auth-split-social-row--single">
          <GoogleAuthButton mode="signin" />
        </div>
      </motion.div>

      <motion.p
        className="auth-split-links"
        style={{ textAlign: 'center', marginTop: '1.5rem', marginBottom: 0, color: '#64748b' }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.15, duration: 0.4 }}
      >
        Don&apos;t have an account? <Link to="/register">Create account</Link>
      </motion.p>
    </AuthSplitShell>
  );
};

export default Login;
